import React from 'react';
import PropTypes from 'prop-types';
import { useHealthMetrics } from '../hooks/useClaw9';

/**
 * Panel displaying health metrics visualization
 */
function HealthDashboardPanel({ className = '' }) {
    const {
        health,
        metrics,
        components,
        isLoading,
        error,
        refresh,
        triggerCheck
    } = useHealthMetrics(60000); // Poll every minute

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'healthy':
            case 'ok':
            case 'up':
                return 'text-green-600 bg-green-100';
            case 'degraded':
            case 'warning':
                return 'text-yellow-600 bg-yellow-100';
            case 'unhealthy':
            case 'down':
            case 'error':
                return 'text-red-600 bg-red-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'healthy':
            case 'ok':
            case 'up':
                return (
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                );
            case 'degraded':
            case 'warning':
                return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                );
            case 'unhealthy':
            case 'down':
            case 'error':
                return (
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                );
        }
    };

    const formatMetricValue = (value, unit) => {
        if (value === null || value === undefined) return 'N/A';
        if (unit === 'bytes') {
            if (value < 1024) return `${value} B`;
            if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
            if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
            return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
        }
        if (unit === 'percent') return `${value.toFixed(1)}%`;
        if (unit === 'ms') return `${value.toFixed(0)} ms`;
        return String(value);
    };

    if (isLoading && !health) {
        return (
            <div className={`flex items-center justify-center h-64 ${className}`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
        );
    }

    return (
        <div className={className}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <h2 className="text-lg font-medium text-gray-900">System Health</h2>
                    {health && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(health.status)}`}>
                            {health.status || 'Unknown'}
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={refresh}
                        disabled={isLoading}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={() => triggerCheck({ deep: true })}
                        disabled={isLoading}
                        className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                        Run Deep Check
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Overall Status Card */}
            <div className="mb-6 bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full flex items-center justify-center bg-gray-50">
                        {getStatusIcon(health?.status)}
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                            {health?.status === 'healthy' ? 'All Systems Operational' :
                             health?.status === 'degraded' ? 'Some Systems Degraded' :
                             health?.status === 'unhealthy' ? 'System Issues Detected' :
                             'Status Unknown'}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Last checked: {health?.lastCheck ? new Date(health.lastCheck).toLocaleString() : 'N/A'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Metrics Grid */}
            {metrics && (
                <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">System Metrics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {metrics.cpu !== undefined && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="text-sm text-gray-500">CPU Usage</div>
                                <div className="mt-1 text-2xl font-semibold text-gray-900">
                                    {formatMetricValue(metrics.cpu, 'percent')}
                                </div>
                                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full ${
                                            metrics.cpu > 80 ? 'bg-red-500' :
                                            metrics.cpu > 60 ? 'bg-yellow-500' : 'bg-green-500'
                                        }`}
                                        style={{ width: `${Math.min(100, metrics.cpu)}%` }}
                                    />
                                </div>
                            </div>
                        )}
                        {metrics.memory !== undefined && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="text-sm text-gray-500">Memory Usage</div>
                                <div className="mt-1 text-2xl font-semibold text-gray-900">
                                    {formatMetricValue(metrics.memory, 'percent')}
                                </div>
                                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full ${
                                            metrics.memory > 85 ? 'bg-red-500' :
                                            metrics.memory > 70 ? 'bg-yellow-500' : 'bg-green-500'
                                        }`}
                                        style={{ width: `${Math.min(100, metrics.memory)}%` }}
                                    />
                                </div>
                            </div>
                        )}
                        {metrics.disk !== undefined && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="text-sm text-gray-500">Disk Usage</div>
                                <div className="mt-1 text-2xl font-semibold text-gray-900">
                                    {formatMetricValue(metrics.disk, 'percent')}
                                </div>
                                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full ${
                                            metrics.disk > 90 ? 'bg-red-500' :
                                            metrics.disk > 75 ? 'bg-yellow-500' : 'bg-green-500'
                                        }`}
                                        style={{ width: `${Math.min(100, metrics.disk)}%` }}
                                    />
                                </div>
                            </div>
                        )}
                        {metrics.responseTime !== undefined && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="text-sm text-gray-500">Response Time</div>
                                <div className="mt-1 text-2xl font-semibold text-gray-900">
                                    {formatMetricValue(metrics.responseTime, 'ms')}
                                </div>
                                <div className="mt-2 text-xs text-gray-400">
                                    {metrics.responseTime < 100 ? 'Excellent' :
                                     metrics.responseTime < 300 ? 'Good' :
                                     metrics.responseTime < 1000 ? 'Fair' : 'Poor'}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Components Status */}
            {components && components.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700">Component Status</h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {components.map((component, index) => (
                            <div key={component.id || index} className="px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {getStatusIcon(component.status)}
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{component.name}</p>
                                        {component.description && (
                                            <p className="text-xs text-gray-500">{component.description}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    {component.responseTime !== undefined && (
                                        <span className="text-xs text-gray-500">
                                            {formatMetricValue(component.responseTime, 'ms')}
                                        </span>
                                    )}
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(component.status)}`}>
                                        {component.status || 'Unknown'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!components && !metrics && health && (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <p className="text-sm text-gray-500">
                        Detailed metrics are being collected. Run a deep check for more information.
                    </p>
                </div>
            )}
        </div>
    );
}

HealthDashboardPanel.propTypes = {
    className: PropTypes.string
};

export default HealthDashboardPanel;
